import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ConnectedProps } from "react-redux";
import { Button, Card, Empty, Modal, Skeleton, Typography } from "antd";
import { CheckOutlined } from "@ant-design/icons";

import connector from "./Container";
import FilterButtons from "../../../../shared/FilterButtons";
import CircleProgress from "../../../../shared/CircleProgress";
import { ReactComponent as CourseIcon } from "../../../../../assets/images/reusable-items/course.svg";
import { ReactComponent as ModuleIcon } from "../../../../../assets/images/reusable-items/module.svg";

const { Text } = Typography;

const UserModuleProgress = (props: ConnectedProps<typeof connector>) => {
  const {
    userModule,
    loading,
    actions: { getUserDetailsStatistics },
  } = props;
  const { id } = useParams();

  const [filteredModules, setFilteredModules] = useState(userModule);
  const [filter, setFilter] = useState("ALL");
  const filterProps = [
    { key: "All Modules", value: "ALL" },
    { key: "In Progress", value: "UNFINISHED" },
    { key: "Finished", value: "FINISHED" },
  ];
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [modalCourses, setModalCourses] = useState<any>([]);

  useEffect(() => {
    id && getUserDetailsStatistics({ type: "module", userId: id });
  }, []);

  useEffect(() => {
    let filtered = userModule;

    if (filter === "FINISHED") {
      filtered = filtered.filter((module: any) => module.progress === 100);
    } else if (filter === "UNFINISHED") {
      filtered = filtered.filter((module: any) => module.progress !== 100);
    }

    setFilteredModules(filtered);
  }, [filter, userModule]);

  const handleFilterChange = (newFilter: string) => {
    setFilter(newFilter);
  };

  const openModal = (courses: any) => {
    setModalCourses(courses);
    setIsModalVisible(true);
  };

  if (loading) {
    return <Skeleton active />;
  }

  return (
    <>
      <div>
        <div className="flex justify-between items-center">
          <Text className="text-xl text-primary-700 font-semibold">
            Module Progress
          </Text>
          <Link
            to={`/educator/user/${id}/details`}
            className="text-sm font-semibold text-primary-700"
          >
            {"< Back to User Details"}
          </Link>
        </div>
        <FilterButtons
          selectedFilter={filter}
          handleFilterChange={handleFilterChange}
          filterOptions={filterProps}
        />
        <div className="flex flex-wrap gap-5 mt-4 overflow-y-scroll max-h-[calc(100vh-265px)]">
          {filteredModules?.length > 0 ? (
            filteredModules.map((content: any, index: number) => (
              <Card key={index} className="w-[calc(33.33%-14px)] p-4">
                <div className="flex gap-4">
                  <div className="flex items-center">
                    <CircleProgress size={100} percentage={content.progress} />
                  </div>
                  <div className="w-full">
                    <div className="text-neutral-600 font-semibold min-h-12 line-clamp-2">
                      {content?.moduleTitle}
                    </div>

                    <div className="flex gap-2 items-center mt-1">
                      <div className="w-4 h-4">
                        <CourseIcon className="w-full h-full fill-primary-400" />
                      </div>
                      <Text className="text-sm text-neutral-600">
                        {content.remainingCourses === "0" ? (
                          <>Finished</>
                        ) : content.remainingCourses === "1" ? (
                          <>1 Course remain</>
                        ) : (
                          <>{content.remainingCourses} Courses remain</>
                        )}
                      </Text>
                    </div>
                    <div className="flex gap-2 mt-3 ">
                      <Text
                        className={`w-[40%]  h-[30px] text-xs text-center bg-rose-400 text-white rounded-lg flex justify-center items-center`}
                      >
                        {content.pointsCompleted == content.totalPoints ? (
                          <>{content.totalPoints} P</>
                        ) : (
                          <>
                            {content.pointsCompleted}/{content.totalPoints} P
                          </>
                        )}
                      </Text>

                      <Button
                        onClick={() => openModal(content?.courses)}
                        className={`w-[60%] h-[30px] bg-primary-600 text-white rounded-lg text-[12px]`}
                      >
                        View Courses
                      </Button>
                    </div>
                  </div>
                </div>
              </Card>
            ))
          ) : (
            <div className="flex items-center w-full justify-center h-full ">
              <Card className="shadow-md p-[60px] my-[40px]">
                <div className="flex flex-col gap-10 items-center justify-center">
                  <div className="h-[60px]">
                    <ModuleIcon className="w-full h-full fill-primary-400" />
                  </div>
                  <Text className="text-neutral-600 text-xl font-bold text-center">
                    There are currently no
                    <div>available Modules</div>
                  </Text>
                </div>
              </Card>
            </div>
          )}
        </div>
      </div>

      <Modal
        open={isModalVisible}
        onOk={() => setIsModalVisible(false)}
        onCancel={() => setIsModalVisible(false)}
        closeIcon={null}
      >
        {modalCourses.length > 0 ? (
          <>
            <div className="flex justify-between items-center pb-4 mb-4 border-b border-b-neutral-200">
              <div className="flex gap-3 items-center">
                <CourseIcon className="fill-primary-400 w-[20px] h-[20px]" />
                <Text className="text-neutral-400 font-normal text-sm">
                  Course(s)
                </Text>
              </div>
            </div>
            <div className="flex flex-col max-h-[calc(100vh-300px)] overflow-y-auto">
              {modalCourses.map((course: any) => (
                <div
                  key={course?.courseId}
                  className={`flex items-center px-4 py-3 mb-2 rounded-lg  ${
                    course?.isCompleted ? "bg-primary-100" : "bg-neutral-100"
                  } `}
                >
                  <Text
                    className={`flex-1 text-neutral-600 text-sm ${
                      course?.isCompleted ? "font-semibold" : ""
                    }`}
                  >
                    {course?.courseTitle}
                  </Text>
                  {course?.isCompleted && (
                    <CheckOutlined className="text-blue-500" />
                  )}
                </div>
              ))}
            </div>
          </>
        ) : (
          <Empty className="py-5" image={Empty.PRESENTED_IMAGE_SIMPLE} />
        )}
      </Modal>
    </>
  );
};

export default UserModuleProgress;
